/**
 * Date/Time Utilities with Timezone Support
 * Supports firm-wide timezone override or browser auto-detection
 */

// Cache for timezone settings
let timezoneSettings = null;
let timezoneSettingsPromise = null;

// Fetch timezone settings from API (cached)
export const fetchTimezoneSettings = async () => {
  if (timezoneSettings) return timezoneSettings;

  if (timezoneSettingsPromise) return timezoneSettingsPromise;

  timezoneSettingsPromise = fetch('/api/timezone')
    .then(res => res.ok ? res.json() : null)
    .then(data => {
      timezoneSettings = data || {
        timezone: 'America/New_York',
        timezone_override_enabled: true,
        time_format: '12h'
      };
      return timezoneSettings;
    })
    .catch(() => {
      timezoneSettings = {
        timezone: 'America/New_York',
        timezone_override_enabled: true,
        time_format: '12h'
      };
      return timezoneSettings;
    });

  return timezoneSettingsPromise;
};

// Initialize timezone settings on load
fetchTimezoneSettings();

// Get the effective timezone (firm override or browser)
export const getEffectiveTimezone = () => {
  // If settings haven't loaded yet, default to firm timezone
  if (!timezoneSettings) return 'America/New_York';
  if (timezoneSettings.timezone_override_enabled && timezoneSettings.timezone) {
    return timezoneSettings.timezone;
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Get user's browser timezone
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Get firm timezone (if set)
export const getFirmTimezone = () => {
  return timezoneSettings?.timezone || 'America/New_York';
};

// Check if firm override is enabled
export const isFirmTimezoneOverride = () => {
  return timezoneSettings?.timezone_override_enabled || false;
};

// Get timezone abbreviation (e.g., "EST", "PST")
export const getTimezoneAbbr = () => {
  const tz = getEffectiveTimezone();
  const date = new Date();
  const timeString = date.toLocaleTimeString('en-US', { timeZone: tz, timeZoneName: 'short' });
  const match = timeString.match(/[A-Z]{2,5}$/);
  return match ? match[0] : '';
};

// Refresh timezone settings (call after updating settings)
export const refreshTimezoneSettings = () => {
  timezoneSettings = null;
  timezoneSettingsPromise = null;
  return fetchTimezoneSettings();
};

// Parse date string (handles ISO strings from API)
const parseDate = (dateInput) => {
  if (!dateInput) return null;
  if (dateInput instanceof Date) return dateInput;

  // Handle ISO string - ensure it's treated as UTC if no timezone specified
  const dateStr = String(dateInput);
  if (dateStr.includes('T') && !dateStr.includes('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
    return new Date(dateStr + 'Z');
  }
  return new Date(dateStr);
};

// Format date only (e.g., "Dec 6, 2025")
export const formatDate = (dateInput, options = {}) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return '—';

  const tz = getEffectiveTimezone();
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
    ...options
  };

  return date.toLocaleDateString('en-US', defaultOptions);
};

// Format short date (e.g., "12/6/25")
export const formatShortDate = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return '—';

  const tz = getEffectiveTimezone();
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    timeZone: tz
  });
};

// Format time only (e.g., "3:45 PM")
export const formatTime = (dateInput, options = {}) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return '—';

  const tz = getEffectiveTimezone();
  const use12h = timezoneSettings?.time_format !== '24h';
  const defaultOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: use12h,
    timeZone: tz,
    ...options
  };

  return date.toLocaleTimeString('en-US', defaultOptions);
};

// Format date and time (e.g., "Dec 6, 2025, 3:45 PM")
export const formatDateTime = (dateInput, options = {}) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return '—';

  const tz = getEffectiveTimezone();
  const use12h = timezoneSettings?.time_format !== '24h';
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: use12h,
    timeZone: tz,
    ...options
  };

  return date.toLocaleString('en-US', defaultOptions);
};

// Format relative time (e.g., "2 hours ago", "in 3 days")
export const formatRelative = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return '—';

  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  const isFuture = diffMs < 0;
  const absDiffMin = Math.abs(diffMin);
  const absDiffHour = Math.abs(diffHour);
  const absDiffDay = Math.abs(diffDay);

  if (Math.abs(diffSec) < 60) {
    return 'just now';
  } else if (absDiffMin < 60) {
    return isFuture ? `in ${absDiffMin}m` : `${absDiffMin}m ago`;
  } else if (absDiffHour < 24) {
    return isFuture ? `in ${absDiffHour}h` : `${absDiffHour}h ago`;
  } else if (absDiffDay < 7) {
    return isFuture ? `in ${absDiffDay}d` : `${absDiffDay}d ago`;
  } else {
    return formatDate(date);
  }
};

// Format for input fields (YYYY-MM-DD)
export const formatForInput = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Format for datetime-local input (YYYY-MM-DDTHH:MM)
export const formatForDateTimeInput = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Convert local datetime to UTC ISO string for API
export const toUTCISO = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return null;
  return date.toISOString();
};

// Check if date is today
export const isToday = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return false;

  const today = new Date();
  return date.toDateString() === today.toDateString();
};

// Check if date is in the past
export const isPast = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return false;
  return date < new Date();
};

// Check if date is overdue (past and not today)
export const isOverdue = (dateInput) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

// Get start of day in local timezone
export const startOfDay = (dateInput = new Date()) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return null;

  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

// Get end of day in local timezone
export const endOfDay = (dateInput = new Date()) => {
  const date = parseDate(dateInput);
  if (!date || isNaN(date.getTime())) return null;

  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

export default {
  getUserTimezone,
  getTimezoneAbbr,
  formatDate,
  formatShortDate,
  formatTime,
  formatDateTime,
  formatRelative,
  formatForInput,
  formatForDateTimeInput,
  toUTCISO,
  isToday,
  isPast,
  isOverdue,
  startOfDay,
  endOfDay
};
